import {ReactElement, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";

import {getCampagnes as fetchCampagnes} from "@/Api/campagne";
import IconAdd from "@/assets/iconAdd.svg?react";
import IconCopy from "@/assets/iconCopy.svg?react";
import IconUpdate from "@/assets/iconUpdate.svg?react";
import {BlocContent} from "@/components/BlocContent/BlocContent";
import {Button} from "@/components/Button/Button";
import {CampagneHistoriqueElement} from "@/components/Campagne/CampagneHistoriqueElement";
import {ErrorBoundary} from "@/components/ErrorBoundary/ErrorBoundary";
import {LoadingOrError} from "@/components/LoadingOrError/LoadingOrError";
import {Title} from "@/components/Title/Title";
import {AppContext} from "@/context/AppContext";
import {LogContext, LogContextType} from "@/context/LogContex";
import {quicksandSemiBold} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useIsConnectedTest} from "@/hooks/useIsConnectedTest/useIsConnectedTest";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {AppContext as AppContextType} from "@/ts/appContext";
import {CampagneKpi} from "@/ts/kpi";

const StatusTitle = styled.div<{$status: string}>`
  width: 100%;
  container: StatusTitle / inline-size;
  margin-top: 20px;
  & div {
    width: 87%;
    background-color: ${({$status, theme}) => {
      switch ($status) {
        case "a venir":
          return theme.colors.lightPrimary;
        case "en cours":
          return theme.colors.mediumPrimary;
        case "passé":
          return theme.colors.darkPrimary;
        default:
          return theme.colors.darkPrimary;
      }
    }};
    @container StatusTitle (width < 1100px) {
      width: 100%;
    }
  }
  & p {
    font-size: 1.25rem;
    color: white;
    text-align: center;
    padding: 7px 0;
    ${quicksandSemiBold};
  }
`;

const ContentButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  height: 100%;

  & button {
    width: 100%;
  }
  @container HistoriqueBloc (width < 1100px) {
    column-gap: 40px;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    & button {
      width: auto;
      min-width: 125px;
      @container HistoriqueBloc (width < 320px) {
        min-width: 0px;
        & span {
          display: none;
        }
        & svg {
          width: 0.875rem;
        }
      }
    }
  }
`;

export function HistoriqueKpi(): ReactElement | null {
  const navigate = useNavigate();
  const {droits} = useGetContext<LogContextType>(LogContext, "LogContext");
  const {API} = useGetContext<AppContextType>(AppContext, "AppContext");
  // Vérifie si l'utilisateur est connecté et redirige vers la page de connexion si ce n'est pas le cas
  const isAuthorized = useIsConnectedTest();
  const loadingState = useLoadingState();
  const [campagnesKPI, setCampagnesKPI] = useState<CampagneKpi[] | null>(null);
  const getCampagnes = async (): Promise<void> => {
    try {
      const {campagnes} = await loadingState.executeWithLoading<{
        campagnes: CampagneKpi[];
      }>(() => fetchCampagnes(API, "Collaborateur"));
      setCampagnesKPI([...campagnes]);
    } catch (error) {
      loadingState.setError(
        `Erreur lors de la récupération des campagnes: ${error}`
      );
    }
  };
  useEffect(() => {
    getCampagnes();
  }, []);
  useEffect(() => {
    console.log(droits);
  }, [droits]);
  // si l'utilisateur n'est pas connecté, on ne rend pas la page
  if (!isAuthorized) {
    return null;
  }
  if (!droits) {
    return null;
  } else if (!droits?.["coact.campagne.lister"]) {
    return <p>Vous n'avez pas les droits pour accéder à cette page</p>;
  }

  const now = new Date();
  const nowMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  const campagnesKpiAVenir = campagnesKPI?.filter(campagne => {
    return new Date(campagne.debut) > now;
  });

  const campagnesKpiPasse = campagnesKPI?.filter(campagne => {
    const fin = new Date(campagne.fin);
    const finMidnight = new Date(
      fin.getFullYear(),
      fin.getMonth(),
      fin.getDate()
    );
    return finMidnight.getTime() < nowMidnight.getTime();
  });

  const campagnesKpiEnCours = campagnesKPI?.filter(campagne => {
    const fin = new Date(campagne.fin);
    const finMidnight = new Date(
      fin.getFullYear(),
      fin.getMonth(),
      fin.getDate()
    );
    return (
      finMidnight.getTime() >= nowMidnight.getTime() &&
      new Date(campagne.debut) < now
    );
  });
  return (
    <BlocContent>
      <Title title="Gestion des campagnes Kpi">
        <Button
          label="CRÉER UN NOUVEAU PLAN KPI"
          icon={IconAdd}
          onClick={() => {
            navigate("/gestion_kpi/creation");
          }}
        />
      </Title>
      <LoadingOrError
        loadingState={loadingState}
        errorImage="/path/to/error-image.png"
        buttonToRefresh={true}
        onRetry={async () => await getCampagnes()}
        data={campagnesKPI}
        dataValidator={data => data && data?.[0]?.kpis && data?.[0]?.nom}>
        <ErrorBoundary
          fallbackText="Erreur lors de l'affichage des campagnes KPI"
          buttonToRefresh={true}>
          <>
            <StatusTitle $status="a venir">
              <div>
                <p>A venir</p>
              </div>
            </StatusTitle>

            {/* Affiche les campagnes a venir*/}
            {campagnesKpiAVenir?.length && campagnesKpiAVenir?.length > 0
              ? campagnesKpiAVenir?.map(campagne => {
                  return (
                    <CampagneHistoriqueElement
                      key={campagne.campagne_id}
                      campagne={campagne}
                      status={"a venir"}
                      refreshCampagnes={() => {
                        getCampagnes();
                      }}>
                      <ContentButton>
                        {droits?.["coact.campagne.modifier"] && (
                          <Button
                            label="MODIFIER"
                            icon={IconUpdate}
                            onClick={() => {
                              navigate(
                                `/gestion_kpi/modification/${campagne.campagne_id}`
                              );
                            }}
                          />
                        )}
                        {droits?.["coact.campagne.creer"] && (
                          <Button
                            label="COPIER"
                            icon={IconCopy}
                            onClick={() => {
                              navigate(
                                `/gestion_kpi/creation/${campagne.campagne_id}`
                              );
                            }}
                          />
                        )}
                      </ContentButton>
                    </CampagneHistoriqueElement>
                  );
                })
              : null}
            <StatusTitle $status="en cours">
              <div>
                <p>En cours</p>
              </div>
            </StatusTitle>

            {/* Affiche les campagnes en cours*/}
            {campagnesKpiEnCours?.length && campagnesKpiEnCours?.length > 0
              ? campagnesKpiEnCours?.map(campagne => {
                  return (
                    <CampagneHistoriqueElement
                      key={campagne.campagne_id}
                      campagne={campagne}
                      status={"en cours"}
                      refreshCampagnes={() => {
                        getCampagnes();
                      }}>
                      <ContentButton>
                        {droits?.["coact.campagne.modifier"] && (
                          <Button
                            label="MODIFIER"
                            icon={IconUpdate}
                            onClick={() => {
                              navigate(
                                `/gestion_kpi/modification/${campagne.campagne_id}`
                              );
                            }}
                          />
                        )}
                        {droits?.["coact.campagne.creer"] && (
                          <Button
                            label="COPIER"
                            icon={IconCopy}
                            onClick={() => {
                              navigate(
                                `/gestion_kpi/creation/${campagne.campagne_id}`
                              );
                            }}
                          />
                        )}
                      </ContentButton>
                    </CampagneHistoriqueElement>
                  );
                })
              : null}
            <StatusTitle $status="passé">
              <div>
                <p>Passé</p>
              </div>
            </StatusTitle>

            {/* Affiche les campagnes passées*/}
            {campagnesKpiPasse?.length && campagnesKpiPasse?.length > 0
              ? campagnesKpiPasse?.map(campagne => {
                  return (
                    <CampagneHistoriqueElement
                      key={campagne.campagne_id}
                      campagne={campagne}
                      status={"passe"}
                      refreshCampagnes={() => {
                        getCampagnes();
                      }}>
                      <ContentButton>
                        {droits?.["coact.campagne.modifier"] && (
                          <Button
                            label="MODIFIER"
                            icon={IconUpdate}
                            onClick={() => {
                              navigate(
                                `/gestion_kpi/modification/${campagne.campagne_id}`
                              );
                            }}
                          />
                        )}
                        {droits?.["coact.campagne.creer"] && (
                          <Button
                            label="COPIER"
                            icon={IconCopy}
                            onClick={() => {
                              navigate(
                                `/gestion_kpi/creation/${campagne.campagne_id}`
                              );
                            }}
                          />
                        )}
                      </ContentButton>
                    </CampagneHistoriqueElement>
                  );
                })
              : null}
          </>
        </ErrorBoundary>
      </LoadingOrError>
    </BlocContent>
  );
}
